/**
 * StaffPicker
 *
 * Lightweight controlled-input + result-list picker that surfaces existing
 * auth users so the parent can attach them to an active reunification
 * event via `useCreateEventStaff`. Mirrors the `usePlanningStudents`
 * pattern (school-scoped search, debounced query, capped page size) but
 * targets the user-detailed endpoint because staff are auth users (not
 * RosterPersons) — see ADR-064 §LAYER 3 for why students/guardians and
 * staff use different identity sources.
 *
 * Scope (v1): searches active users only and excludes any user already
 * assigned to this event (the parent passes `excludeUserIds`). Ad-hoc
 * staff creation is explicitly out of scope — that requires a new
 * auth-user flow and identity verification policy.
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Center,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CardButton } from '@themes/aegix';
import { useUsers } from '../../hooks/queries';
import type { UserFormInitialValues } from '../../types/UserFormInitialValue';

const PICKER_PAGE_SIZE = 20;

export interface StaffPickerSelection {
  userId: number;
  name: string;
  email: string;
}

export interface StaffPickerProps {
  /** Auth-user ids already on the event — filtered out of results. */
  excludeUserIds?: number[];
  /** The currently picked user (controlled). */
  selected: StaffPickerSelection | null;
  /** Called when the user clicks a row. */
  onSelect: (selection: StaffPickerSelection) => void;
  /** Optional placeholder for the search input. */
  placeholder?: string;
  /** Optional fixed height for the result list (default 240). */
  listHeight?: number;
  /** Optional data-e2e prefix to keep parents' selectors stable. */
  dataE2ePrefix?: string;
}

export function StaffPicker({
  excludeUserIds,
  selected,
  onSelect,
  placeholder = 'Search staff by name or email…',
  listHeight = 240,
  dataE2ePrefix = 'staff-picker',
}: StaffPickerProps) {
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching, error } = useUsers({
    search: search || undefined,
    is_active: true,
    limit: PICKER_PAGE_SIZE,
    ordering: 'first_name',
  });

  const excludeSet = useMemo(
    () => new Set(excludeUserIds ?? []),
    [excludeUserIds],
  );

  const candidates = useMemo<UserFormInitialValues[]>(() => {
    const all = data?.results ?? [];
    return all.filter((u) => {
      const id = Number(u.id);
      if (!Number.isFinite(id) || id <= 0) return false;
      return !excludeSet.has(id);
    });
  }, [data?.results, excludeSet]);

  return (
    <VStack align="stretch" gap={3}>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder.replace(/[….]+$/, '')}
        size="sm"
        data-e2e={`${dataE2ePrefix}-search`}
      />

      <Box
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        height={`${listHeight}px`}
        overflowY="auto"
        data-e2e={`${dataE2ePrefix}-list`}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="sm" color="button.primary" />
          </Center>
        ) : error ? (
          <Center height="100%" px={3}>
            <Text fontSize="sm" color="text.danger">
              {error instanceof Error
                ? error.message
                : 'Failed to load staff'}
            </Text>
          </Center>
        ) : candidates.length === 0 ? (
          <Center height="100%">
            {isFetching ? (
              <Spinner size="sm" color="button.primary" />
            ) : (
              <Text fontSize="sm" color="text.tertiary">
                {search
                  ? 'No matching staff found'
                  : 'No staff available to add'}
              </Text>
            )}
          </Center>
        ) : (
          <VStack align="stretch" gap={0}>
            {candidates.map((user) => {
              const userId = Number(user.id);
              const fullName =
                `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
                user.email;
              const isSelected = selected?.userId === userId;
              return (
                <CardButton
                  key={userId}
                  variant="row"
                  selected={isSelected}
                  onClick={() =>
                    onSelect({
                      userId,
                      name: fullName,
                      email: user.email,
                    })
                  }
                  description={user.email}
                  data-e2e={`${dataE2ePrefix}-row-${userId}`}
                >
                  {fullName}
                </CardButton>
              );
            })}
          </VStack>
        )}
      </Box>

      {selected ? (
        <Text
          fontSize="xs"
          color="text.secondary"
          data-e2e={`${dataE2ePrefix}-selected-summary`}
        >
          Selected: {selected.name}
        </Text>
      ) : null}
    </VStack>
  );
}

export default StaffPicker;
